import { useMemo, type ReactNode } from "react";
import { GreetingScreen } from "@trillion/ui/components/chat/greeting-screen";
import { MessageList } from "@trillion/ui/components/chat/message-list";
import { ChatInput } from "@trillion/ui/components/chat/chat-input";
import { DEFAULT_SUGGESTIONS } from "@/components/brand/default-suggestions";
import { DISCLAIMER_TEXT } from "@/lib/constants";
import type { ChatMessage } from "@/hooks/use-chat-stream";

/**
 * Convert ChatMessage[] to the parts-based format expected by MessageList.
 */
export function toMessageParts(messages: ChatMessage[]) {
  return messages.map((m) => {
    const parts: { type: string; [key: string]: unknown }[] = [];

    // Add text part if there is content
    if (m.content) {
      parts.push({ type: "text", text: m.content });
    }

    // Add tool call parts
    for (const tc of m.toolCalls ?? []) {
      if (tc.result !== undefined) {
        parts.push({
          type: "tool-invocation",
          state: "output-available",
          toolCallId: tc.id,
          toolName: tc.name,
          args: tc.input,
          output: tc.result,
        });
      } else {
        parts.push({
          type: "tool-invocation",
          state: "pending",
          toolCallId: tc.id,
          toolName: tc.name,
          args: tc.input,
        });
      }
    }

    // Ensure at least one part exists
    if (parts.length === 0) {
      parts.push({ type: "text", text: "" });
    }

    return {
      id: m.id,
      role: m.role,
      parts,
    };
  });
}

/**
 * Choose the greeting chips: nonempty traffic-intent chips replace the defaults wholesale (each chip's
 * text is both its label and its prompt); absent or empty ⇒ help-web's DEFAULT_SUGGESTIONS.
 */
export function resolveSuggestions(suggestionChips?: string[]): { label: string; prompt: string }[] {
  return suggestionChips?.length
    ? suggestionChips.map((chip) => ({ label: chip, prompt: chip }))
    : DEFAULT_SUGGESTIONS;
}

export interface ChatContainerProps {
  messages: ChatMessage[];
  input: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  onSuggestionSelect: (prompt: string) => void;
  isLoading: boolean;
  /** trafficIntent?.greeting ?? <DefaultGreeting /> — resolved by the layout. */
  greeting: ReactNode;
  /** trafficIntent?.suggestion_chips — absent or empty ⇒ help-web's DEFAULT_SUGGESTIONS are used. */
  suggestionChips?: string[];
}

/**
 * The chat surface: greeting-or-transcript plus the composer, driven entirely by props.
 *
 * Presentational by design — the session, intent, storage, traffic context and stream all live in
 * ChatLayout, which is rendered once above both routes so the `/` → `/c/<id>` hop never remounts
 * them. No wrapper element either: ChatAppShell owns the viewport and the scroll bounds.
 */
export function ChatContainer({
  messages,
  input,
  onInputChange,
  onSend,
  onStop,
  onSuggestionSelect,
  isLoading,
  greeting,
  suggestionChips,
}: ChatContainerProps) {
  // Intent chips (served as a real JSON array) when nonempty, otherwise help-web's defaults
  const suggestions = useMemo(() => resolveSuggestions(suggestionChips), [suggestionChips]);

  // Convert messages to the parts-based format expected by MessageList
  const messageParts = useMemo(() => toMessageParts(messages), [messages]);

  if (messages.length === 0) {
    return (
      <GreetingScreen
        input={input}
        onInputChange={onInputChange}
        onSend={onSend}
        onStop={onStop}
        isLoading={isLoading}
        onSuggestionSelect={onSuggestionSelect}
        greeting={greeting}
        suggestions={suggestions}
      />
    );
  }

  return (
    <>
      <MessageList messages={messageParts} isLoading={isLoading} />
      {/* GreetingScreen's own ChatInput passes `greeting`, and chat-input.tsx renders the fine
          print only under !greeting — so the disclaimer belongs to the transcript branch alone. */}
      <ChatInput
        input={input}
        onInputChange={onInputChange}
        onSend={onSend}
        onStop={onStop}
        isLoading={isLoading}
        disclaimer={DISCLAIMER_TEXT}
      />
    </>
  );
}
